import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

// Import images
import landing1 from '../imgs/landing1.png';
import landing2 from '../imgs/landing2.jpg';
import landing3 from '../imgs/landing3.png';
import landing4 from '../imgs/landing4.png';
import landing5 from '../imgs/landing5.jpg';

const LandingPage = () => {
  return (
    <div className="landing-page">
      {/* Navigation Header */}
      <nav className="navbar">
        <div className="nav-content">
          <div className="logo">
            <h2>PodTalk</h2>
          </div>
          <div className="nav-links">
            <Link to="/product" className="nav-link">Try PodTalk</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Transform Your Information Overload into Effortless Audio Insights</h1>
          <h2>Stay Informed, Not Overwhelmed</h2>
          <p className="hero-subtitle">
            PodTalk delivers personalized news summaries and deep research insights in clear, 
            accessible audio format. Powered by advanced AI, we transform how you consume information 
            – making it effortless, engaging, and perfectly tailored to your interests.
          </p>
          <div className="cta-container">
            <Link to="/product" className="cta-button primary">
              Discover Your Personalized Audio Feed
            </Link>
            <Link to="/product" className="cta-button secondary">
              Try PodTalk Now
            </Link>
          </div>
        </div>
        <div className="hero-visual">
          <img src={landing1} alt="AI-powered audio news transformation" className="hero-image" />
          <div className="floating-elements">
            <div className="float-element element-1"></div>
            <div className="float-element element-2"></div>
            <div className="float-element element-3"></div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="value-props">
        <div className="section-header">
          <h2>Why Choose PodTalk?</h2>
          <p>Experience the future of information consumption</p>
        </div>
        <div className="value-grid">
          <div className="value-prop-card">
            <div className="icon">
              <img src={landing2} alt="Save time with AI summaries" className="value-icon" />
            </div>
            <h3>Save Time</h3>
            <p>Reclaim hours of your day with AI-powered summaries that distill complex information into digestible insights</p>
          </div>
          <div className="value-prop-card">
            <div className="icon">
              <img src={landing3} alt="Personalized content curation" className="value-icon" />
            </div>
            <h3>Personalized For You</h3>
            <p>Tailored content that matches your interests and professional needs, ensuring every minute of listening adds value</p>
          </div>
          <div className="value-prop-card">
            <div className="icon">
              <img src={landing4} alt="Listen anywhere, anytime" className="value-icon" />
            </div>
            <h3>Listen Anywhere</h3>
            <p>Perfect for commuting, exercising, or multitasking – consume information passively while living your life</p>
          </div>
          <div className="value-prop-card">
            <div className="icon">
              <img src={landing5} alt="Deep understanding through AI research" className="value-icon" />
            </div>
            <h3>Understand Deeper</h3>
            <p>Go beyond surface-level news with comprehensive research and analysis that provides true understanding</p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="section-header">
          <h2>How PodTalk Works</h2>
          <p>Three simple steps to transform your information consumption</p>
        </div>
        <div className="steps">
          <div className="step">
            <div className="step-visual">
              <div className="step-number">1</div>
            </div>
            <h3>Define Your Interests</h3>
            <p>Tell us what topics matter to you or search for specific subjects you want to explore</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-visual">
              <div className="step-number">2</div>
            </div>
            <h3>AI Gathers & Summarizes</h3>
            <p>Our advanced AI processes information from multiple sources and creates comprehensive summaries</p>
          </div>
          <div className="step-connector"></div>
          <div className="step">
            <div className="step-visual">
              <div className="step-number">3</div>
            </div>
            <h3>Listen to Your PodTalk</h3>
            <p>Enjoy your personalized audio digest anywhere, anytime – perfectly crafted for your lifestyle</p>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="technology">
        <div className="tech-content">
          <div className="tech-badge">Powered by Advanced AI</div>
          <h2>Leveraging Cutting-Edge AI for Unparalleled Research Depth</h2>
          <p>
            PodTalk harnesses Perplexity AI's sophisticated research capabilities to deliver 
            comprehensive, accurate, and nuanced summaries. Our technology doesn't just aggregate 
            information – it understands context, identifies key insights, and presents them in 
            a format that enhances your understanding and saves your time.
          </p>
          <div className="tech-features">
            <div className="tech-feature">
              <span className="feature-icon">⚡</span>
              <span>Real-time Information Processing</span>
            </div>
            <div className="tech-feature">
              <span className="feature-icon">🔍</span>
              <span>Deep Research Capabilities</span>
            </div>
            <div className="tech-feature">
              <span className="feature-icon">🎯</span>
              <span>Context-Aware Summarization</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <div className="cta-content">
          <h2>Ready to Transform How You Stay Informed?</h2>
          <p>Join thousands of professionals who've already discovered the power of effortless learning</p>
          <Link to="/product" className="cta-button primary large">
            Explore PodTalk Now
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <h3>PodTalk</h3>
              <p>Transforming information consumption through intelligent audio summaries</p>
            </div>
            <div className="footer-links">
              <div className="link-group">
                <h4>Product</h4>
                <Link to="/product">Try PodTalk</Link>
                <Link to="/features">Features</Link>
              </div>
              <div className="link-group">
                <h4>Company</h4>
                <Link to="/about">About Us</Link>
                <Link to="/contact">Contact</Link>
              </div>
              <div className="link-group">
                <h4>Legal</h4>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms of Service</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="copyright">
              © 2024 PodTalk. All rights reserved.
            </div>
            <div className="social-links">
              <button aria-label="Twitter">🐦</button>
              <button aria-label="LinkedIn">💼</button>
              <button aria-label="Email">📧</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 